import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../../lib/api'

// ==================== Query Keys ====================

const TESTS_KEY = 'tests'
const TEST_QUESTIONS_KEY = 'test-questions'
const TEST_ASSIGNMENTS_KEY = 'test-assignments'
const TEST_RESULTS_KEY = 'test-results'
const TEST_RESULTS_SUMMARY_KEY = 'test-results-summary'

// ==================== Types ====================

export interface TestFilters {
  search?: string
  category?: string
  is_active?: string
  page?: number
  limit?: number
  sort?: string
}

export interface Test {
  id: string
  tenant_id: string
  title: string
  title_kk?: string | null
  description?: string | null
  description_kk?: string | null
  category: 'mandatory' | 'certification' | 'training'
  time_limit_minutes: number
  passing_score: number
  max_attempts: number
  cooldown_hours: number
  questions_per_attempt?: number | null
  shuffle_questions: boolean
  shuffle_options: boolean
  is_active: boolean
  created_by?: string | null
  created_at: string
  updated_at: string
  _count?: { test_questions?: number; test_assignments?: number }
  test_questions?: TestQuestion[]
}

export interface QuestionOption {
  id?: string
  text: string
  text_kk?: string
  is_correct: boolean
  sort_order?: number
}

export interface TestQuestion {
  id: string
  test_id: string
  text: string
  text_kk?: string | null
  image_url?: string | null
  question_type: 'single_choice' | 'multiple_choice'
  points: number
  sort_order: number
  is_active: boolean
  created_at: string
  question_options?: QuestionOption[]
}

export interface TestAssignment {
  id: string
  tenant_id: string
  test_id: string
  employee_id?: string | null
  department_id?: string | null
  position_id?: string | null
  is_required: boolean
  deadline?: string | null
  assigned_by?: string | null
  created_at: string
  tests?: { id: string; title: string }
  employees?: { id: string; first_name: string; last_name: string }
  departments?: { id: string; name: string }
  positions?: { id: string; name: string }
}

export interface AssignmentFilters {
  test_id?: string
  department_id?: string
  employee_id?: string
  page?: number
  limit?: number
  sort?: string
}

export interface TestResult {
  employee_id: string
  employee_name: string
  department_name: string
  position_name: string
  test_id: string
  test_title: string
  attempts_used: number
  best_score: number
  last_attempt_at: string | null
  is_passed: boolean
  status: 'passed' | 'failed' | 'in_progress' | 'not_started' | 'blocked'
}

export interface ResultFilters {
  test_id?: string
  department_id?: string
  employee_id?: string
  status?: string
  date_from?: string
  date_to?: string
  search?: string
  page?: number
  limit?: number
  sort?: string
}

export interface TestSummaryItem {
  test_id: string
  title: string
  total_assigned: number
  passed: number
  failed: number
  in_progress: number
  not_started: number
  blocked: number
  average_score: number
  pass_rate: number
}

export interface TestResultsSummary {
  total_employees: number
  tests: TestSummaryItem[]
}

// ==================== Tests Hooks ====================

export const useTests = (filters: TestFilters = {}) => {
  return useQuery({
    queryKey: [TESTS_KEY, filters],
    queryFn: async () => {
      const response = await apiClient.getTests(filters)
      return {
        data: response.data || [],
        total: response.total || 0,
        page: response.page || 1,
        limit: response.limit || 20,
        totalPages: response.totalPages || 1,
      }
    },
    staleTime: 30 * 1000,
  })
}

export const useTest = (id: string) => {
  return useQuery({
    queryKey: [TESTS_KEY, id],
    queryFn: async () => {
      const response = await apiClient.getTest(id)
      return response
    },
    enabled: !!id,
    staleTime: 30 * 1000,
  })
}

export const useCreateTest = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: Partial<Test>) => {
      return apiClient.createTest(data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TESTS_KEY] })
    },
  })
}

export const useUpdateTest = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Test> }) => {
      return apiClient.updateTest(id, data)
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: [TESTS_KEY] })
      queryClient.invalidateQueries({ queryKey: [TESTS_KEY, id] })
    },
  })
}

export const useDeleteTest = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      return apiClient.deleteTest(id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TESTS_KEY] })
    },
  })
}

// ==================== Questions Hooks ====================

export const useTestQuestions = (testId: string) => {
  return useQuery({
    queryKey: [TEST_QUESTIONS_KEY, testId],
    queryFn: async () => {
      const response = await apiClient.getTestQuestions(testId)
      return response
    },
    enabled: !!testId,
    staleTime: 30 * 1000,
  })
}

export const useCreateQuestion = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ testId, data }: { testId: string; data: any }) => {
      return apiClient.createTestQuestion(testId, data)
    },
    onSuccess: (_, { testId }) => {
      queryClient.invalidateQueries({ queryKey: [TEST_QUESTIONS_KEY, testId] })
      queryClient.invalidateQueries({ queryKey: [TESTS_KEY] })
    },
  })
}

export const useUpdateQuestion = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ testId, questionId, data }: { testId: string; questionId: string; data: any }) => {
      return apiClient.updateTestQuestion(testId, questionId, data)
    },
    onSuccess: (_, { testId }) => {
      queryClient.invalidateQueries({ queryKey: [TEST_QUESTIONS_KEY, testId] })
    },
  })
}

export const useDeleteQuestion = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ testId, questionId }: { testId: string; questionId: string }) => {
      return apiClient.deleteTestQuestion(testId, questionId)
    },
    onSuccess: (_, { testId }) => {
      queryClient.invalidateQueries({ queryKey: [TEST_QUESTIONS_KEY, testId] })
      queryClient.invalidateQueries({ queryKey: [TESTS_KEY] })
    },
  })
}

// ==================== Assignments Hooks ====================

export const useTestAssignments = (filters: AssignmentFilters = {}) => {
  return useQuery({
    queryKey: [TEST_ASSIGNMENTS_KEY, filters],
    queryFn: async () => {
      const response = await apiClient.getTestAssignments(filters)
      return {
        data: response.data || [],
        total: response.total || 0,
        page: response.page || 1,
        limit: response.limit || 20,
        totalPages: response.totalPages || 1,
      }
    },
    staleTime: 30 * 1000,
  })
}

export const useCreateAssignment = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: any) => {
      return apiClient.createTestAssignment(data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEST_ASSIGNMENTS_KEY] })
      queryClient.invalidateQueries({ queryKey: [TEST_RESULTS_KEY] })
      queryClient.invalidateQueries({ queryKey: [TEST_RESULTS_SUMMARY_KEY] })
    },
  })
}

export const useDeleteAssignment = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      return apiClient.deleteTestAssignment(id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEST_ASSIGNMENTS_KEY] })
      queryClient.invalidateQueries({ queryKey: [TEST_RESULTS_KEY] })
      queryClient.invalidateQueries({ queryKey: [TEST_RESULTS_SUMMARY_KEY] })
    },
  })
}

// ==================== Results Hooks ====================

export const useTestResults = (filters: ResultFilters = {}) => {
  return useQuery({
    queryKey: [TEST_RESULTS_KEY, filters],
    queryFn: async () => {
      const response = await apiClient.getTestResults(filters)
      return {
        data: response.data || [],
        total: response.total || 0,
        page: response.page || 1,
        limit: response.limit || 20,
        totalPages: response.totalPages || 1,
      }
    },
    staleTime: 30 * 1000,
  })
}

export const useTestResultsSummary = () => {
  return useQuery({
    queryKey: [TEST_RESULTS_SUMMARY_KEY],
    queryFn: async (): Promise<TestResultsSummary> => {
      const response = await apiClient.getTestResultsSummary()
      return response
    },
    staleTime: 60 * 1000,
  })
}

// ==================== My Tests Types (Employee) ====================

export interface MyAssignedTest {
  test_id: string
  title: string
  title_kk?: string | null
  category: 'mandatory' | 'certification' | 'training'
  time_limit_minutes: number
  passing_score: number
  max_attempts: number
  is_required: boolean
  deadline?: string | null
  my_status: {
    attempts_used: number
    best_score: number
    is_passed: boolean
    last_attempt_at: string | null
    can_retry: boolean
    next_retry_at: string | null
    in_progress_attempt_id: string | null
  }
}

export interface StartAttemptResponse {
  attempt_id: string
  attempt_number: number
  test_title: string
  time_limit_minutes: number
  total_questions: number
  started_at: string
  expires_at: string
}

export interface AttemptQuestion {
  question_id: string
  index: number
  text: string
  text_kk?: string | null
  question_type: 'single_choice' | 'multiple_choice'
  image_url?: string | null
  options: { option_id: string; text: string; text_kk?: string | null }[]
  answered: boolean
  selected_options: string[]
}

export interface AttemptData {
  attempt_id: string
  test_title: string
  status: string
  time_remaining_seconds: number
  total_questions: number
  answered_count: number
  questions: AttemptQuestion[]
  timed_out?: boolean
  score?: number
  correct_count?: number
  is_passed?: boolean
}

export interface FinishAttemptResponse {
  attempt_id: string
  status: 'completed' | 'timed_out'
  score: number
  correct_count: number
  total_count: number
  is_passed: boolean
  time_spent_seconds: number
  finished_at: string
}

// ==================== My Tests Hooks (Employee) ====================

const MY_TESTS_KEY = 'my-tests'
const MY_ATTEMPT_KEY = 'my-attempt'

export const useMyTests = () => {
  return useQuery({
    queryKey: [MY_TESTS_KEY],
    queryFn: async () => {
      const response = await apiClient.getMyTests()
      return (response.data || []) as MyAssignedTest[]
    },
    staleTime: 30 * 1000,
  })
}

export const useStartAttempt = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (testId: string): Promise<StartAttemptResponse> => {
      return apiClient.startMyTestAttempt(testId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [MY_TESTS_KEY] })
    },
  })
}

export const useMyAttempt = (attemptId: string | null) => {
  return useQuery({
    queryKey: [MY_ATTEMPT_KEY, attemptId],
    queryFn: async (): Promise<AttemptData> => {
      return apiClient.getMyAttempt(attemptId!)
    },
    enabled: !!attemptId,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  })
}

export const useSubmitAnswer = () => {
  return useMutation({
    mutationFn: async ({ attemptId, questionId, selectedOptions }: {
      attemptId: string
      questionId: string
      selectedOptions: string[]
    }) => {
      return apiClient.submitMyAnswer(attemptId, {
        question_id: questionId,
        selected_options: selectedOptions,
      })
    },
  })
}

export const useFinishAttempt = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (attemptId: string): Promise<FinishAttemptResponse> => {
      return apiClient.finishMyAttempt(attemptId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [MY_TESTS_KEY] })
      queryClient.invalidateQueries({ queryKey: [MY_ATTEMPT_KEY] })
    },
  })
}
